import { useEffect, useRef, useState } from 'react';
import { Send, X } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import {
  useThreadMessages,
  useSendMessage,
  useMarkThreadRead,
} from '@/hooks/useMessages';

interface Props {
  open: boolean;
  onClose: () => void;
  partnerId: string | null;
  partnerUsername: string;
  tradeListingId?: string;
  prefill?: string;
}

export function MessageThreadSheet({
  open,
  onClose,
  partnerId,
  partnerUsername,
  tradeListingId,
  prefill,
}: Props) {
  const { user } = useAuth();
  const { data: messages, isLoading } = useThreadMessages(open ? partnerId : null);
  const send = useSendMessage();
  const markRead = useMarkThreadRead(partnerId);
  const [text, setText] = useState(prefill || '');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open && partnerId) markRead.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, partnerId, messages?.length]);

  useEffect(() => {
    if (open && prefill !== undefined) setText(prefill);
  }, [open, prefill]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  if (!open || !partnerId) return null;

  const handleSend = async () => {
    if (!text.trim()) return;
    try {
      await send.mutateAsync({
        recipientId: partnerId,
        body: text.trim(),
        tradeListingId,
      });
      setText('');
    } catch {/* toast handled by query */}
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-background/80 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="animate-slide-up w-full max-w-[430px] h-[80vh] flex flex-col rounded-t-2xl border border-border bg-card"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div>
            <p className="font-display text-base font-semibold text-foreground">{partnerUsername}</p>
            {tradeListingId && (
              <p className="text-[10px] text-primary font-body">About a trade listing</p>
            )}
          </div>
          <button onClick={onClose} className="text-muted-foreground">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2">
          {isLoading ? (
            <p className="text-center text-xs text-muted-foreground font-body py-8">Loading...</p>
          ) : !messages?.length ? (
            <p className="text-center text-xs text-muted-foreground font-body py-8">
              No messages yet — say hi!
            </p>
          ) : (
            messages.map(m => {
              const mine = m.sender_id === user?.id;
              return (
                <div
                  key={m.id}
                  className={`flex ${mine ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm font-body ${
                      mine
                        ? 'bg-primary text-primary-foreground rounded-br-sm'
                        : 'bg-secondary text-foreground rounded-bl-sm'
                    }`}
                  >
                    {m.body}
                    <div className={`mt-0.5 text-[9px] opacity-60`}>
                      {new Date(m.created_at).toLocaleTimeString([], {
                        hour: 'numeric',
                        minute: '2-digit',
                      })}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center gap-2 border-t border-border p-3">
          <input
            value={text}
            onChange={e => setText(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
            placeholder="Type a message..."
            className="flex-1 rounded-full border border-border bg-secondary px-4 py-2 text-sm text-foreground font-body placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <button
            onClick={handleSend}
            disabled={!text.trim() || send.isPending}
            className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground disabled:opacity-50"
          >
            <Send className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
